import React, { useCallback, useEffect, useMemo } from 'react'
import { append, isEmpty, map, pathOr, remove } from 'ramda'
import { FieldArray, FormikErrors } from 'formik'
import { round } from 'lodash'
import { portfolioApiConstants } from '../../../../../modules/api/portfolio'
import { currencyUtils } from '../../../../../utils'
import {
  Button,
  ControlledTooltip,
  FormField,
  InfoBox,
  PercentageField,
  TextField,
  TextInput,
  Segment,
  CurrencyText,
  CurrencyField,
} from 'components'
import DraggableList from '../../../molecules/DraggableList/DraggableList'
import { leaseTypes } from 'modules/api/portfolio/constants'
import styles from './CommissionSplit.module.scss'
import { $TSFixMe } from 'types/ts-migrate'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { PartySearchOrCreate } from 'views/containers/Search/PartySearchOrCreate'

const { commissionUndertakings } = portfolioApiConstants

// Defined at module scope: declaring it inside render created a new component type every
// render, forcing React to unmount/remount the button (and lose focus/tooltip state).
const ActionButton = ({ tooltip, icon, action, disabled = false }: any) => (
  <ControlledTooltip body={tooltip} place="above">
    <Button disabled={disabled} ghost className={styles[icon]} onClick={action} />
  </ControlledTooltip>
)

type TProps = {
  feeName: string
  defaultResultSets?: Array<Array<{ id: string; text: string }>>
  leaseType: string
  leaseTermInMonths?: number
  setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  handleChange?: (event: React.ChangeEvent<any>) => void
  handleBlur?: (event: React.FocusEvent<any>) => void
  errors?: FormikErrors<$TSFixMe>
  values: {
    type: string
    monthlyRentAmount: number | string
    netAmount: {
      value: number | string
      vatable: boolean
    }
    percentage: {
      value: number | string
      vatable: boolean
    }
    splits: Array<{
      agent: {
        label: string
        value: string
      }
      splitPercentage: number
      isPrimary?: boolean
    }>
  }
  type?: string
}

const CommissionSplit = ({
  feeName,
  defaultResultSets,
  leaseType,
  leaseTermInMonths,
  values,
  errors,
  setFieldValue,
  setFieldTouched,
  handleChange,
  handleBlur,
}: TProps) => {
  const handleSetPrimary = useCallback(
    (targetIndex: number) => {
      // Create a completely new array to ensure proper re-rendering
      const currentSplits = [...values.splits]

      // Get the target split and mark it as primary (preserve all its data including splitPercentage)
      const primarySplit = {
        ...currentSplits[targetIndex],
        isPrimary: true,
      }

      // Get all other splits (excluding the target) and mark them as not primary
      // Preserve all their data including splitPercentage values
      const otherSplits = currentSplits
        .filter((_, index) => index !== targetIndex)
        .map(split => ({
          ...split,
          isPrimary: false,
        }))

      // Create new array with primary split first, followed by other splits
      const reorderedSplits = [primarySplit, ...otherSplits]

      // Update the entire splits array at once
      setFieldValue(`${feeName}.splits`, reorderedSplits, true)
    },
    [values.splits, setFieldValue, feeName],
  )

  const calculatePercentageValue = useCallback(() => {
    const { type, percentage, monthlyRentAmount } = values
    if (type === commissionUndertakings.variable) {
      const percentValue = pathOr(0, ['value'], percentage) / 100
      return leaseType === leaseTypes.unmanaged
        ? parseFloat(monthlyRentAmount as string) * percentValue * (leaseTermInMonths ?? 12)
        : parseFloat(monthlyRentAmount as string) * percentValue
    }
    return 0
  }, [values])

  const getCommissionAmount = useCallback(
    (split: any) => {
      const { type, netAmount, percentage } = values
      const percentageValue = calculatePercentageValue()
      const commissionAmount =
        type === commissionUndertakings.variable
          ? percentageValue
            ? round((percentageValue * split.splitPercentage) / 100)
            : 0
          : netAmount.value
          ? round((parseFloat(netAmount.value as string) * split.splitPercentage) / 100)
          : 0
      const vatable = type === commissionUndertakings.variable ? percentage?.vatable : netAmount?.vatable
      const netCommission = vatable ? currencyUtils.sutractVat(commissionAmount || 0).getAmount() : commissionAmount
      return !isNaN(netCommission) ? netCommission : 0
    },
    [values, calculatePercentageValue],
  )

  const getVatAmount = useCallback(() => {
    const { type, netAmount, percentage } = values
    const percentageValue = calculatePercentageValue()
    const grossCommission =
      type === commissionUndertakings.variable ? round(percentageValue) : round(parseFloat(netAmount.value as string))
    const vatable = type === commissionUndertakings.variable ? percentage?.vatable : netAmount?.vatable
    const netCommission = vatable ? currencyUtils.sutractVat(grossCommission || 0).getAmount() : grossCommission
    const vatAmount = grossCommission - netCommission
    return !isNaN(vatAmount) ? vatAmount : 0
  }, [values, calculatePercentageValue])

  const handleRemoveRow = useCallback(
    (index: number) => {
      const newSplit = remove(index, 1, values.splits)

      // Ensure the first remaining split (index 0) is always primary
      const updatedSplits = newSplit.map((split, i) => ({
        ...split,
        isPrimary: i === 0, // First split becomes primary, others become false
      }))

      setFieldValue(`${feeName}.splits`, updatedSplits)
    },
    [values.splits, setFieldValue, feeName],
  )

  const handleAddRow = useCallback(() => {
    const newRow = {
      agent: { value: '', label: '' },
      splitPercentage: 0,
      isPrimary: false, // New rows are never primary
    }

    // Clear all existing splitPercentage amounts when adding a new row
    // Keep isPrimary true for the first row (index 0), false for all others
    const clearedSplits = values.splits.map((split, index) => ({
      ...split,
      splitPercentage: 0,
      isPrimary: index === 0, // First row stays primary, others become false
    }))

    // Update the entire splits array in a single pass. `newSplits` already carries the
    // cleared splitPercentage/isPrimary values, and each PercentageField resyncs to its
    // `value` prop, so no follow-up per-field writes (or forced remounts) are needed.
    const newSplits = append(newRow, clearedSplits)
    setFieldValue(`${feeName}.splits`, newSplits)
  }, [values.splits, setFieldValue, feeName])

  useEffect(() => {
    if (values.splits.length === 0) {
      handleAddRow()
    }
  }, [values.splits.length, handleAddRow])

  const vatAmount = getVatAmount()

  return (
    <div className={styles.root}>
      {vatAmount > 0 && (
        <Segment>
          <div className={styles.vat}>
            VAT Amount: <CurrencyText>{vatAmount}</CurrencyText>
          </div>
        </Segment>
      )}
      <FieldArray
        name={`${feeName}.splits`}
        render={() => {
          const rows = values.splits.map((s: any, i: any) => {
            const commissionAmount = getCommissionAmount(s)
            const agentName = pathOr('', ['agent', 'label'], s)
            const splitPercentage = pathOr('', ['splitPercentage'], s)
            const isPrimary = pathOr(false, ['isPrimary'], s)

            return {
              itemProps: {
                disableRemove: values.splits.length === 1,
                isDraggable: false,
                style: { height: '90px' },
              },
              children: (
                <div className="rbn--row" key={`split-${s.agent?.value || `empty-${i}`}`}>
                  <div className="rbn--col-sm">
                    <FormField>
                      {!isEmpty(agentName) ? (
                        <TextField
                          inputComponent={
                            <TextInput
                              type={TextFieldTypes.text}
                              name={`${feeName}.splits.${i}.agent`}
                              value={agentName}
                              disabled
                            />
                          }
                          label={i === 0 ? 'Agent' : null}
                        />
                      ) : (
                        <PartySearchOrCreate
                          name={`${feeName}-add-agent-${i}`}
                          label={i === 0 ? 'Agent' : undefined}
                          placeholder="Add Agent"
                          value={agentName}
                          onSelect={(data: any) => {
                            setFieldValue(`${feeName}.splits.${i}`, {
                              splitPercentage,
                              agent: {
                                value: data.id,
                                label: data.text,
                              },
                            })
                          }}
                          defaultResultSets={[]}
                          orderByTags="Agent,Agency"
                          resultSetHeadings={['Your Team', 'All Contacts']}
                          focusOnMount={true}
                          showAddNewContact={true}
                          autoSelectNewContact={true}
                        />
                      )}
                    </FormField>
                  </div>
                  <div className="rbn--col-sm">
                    <FormField>
                      <PercentageField
                        key={`percentage-${s.agent?.value || `empty-${i}`}`}
                        name={`${feeName}.splits.${i}.splitPercentage`}
                        label={i === 0 ? 'Split' : null}
                        value={s.splitPercentage}
                        onFocus={setFieldTouched}
                        onChange={(e: any) => setFieldValue(`${feeName}.splits.${i}.splitPercentage`, e.floatValue)}
                        onBlur={handleBlur}
                        onToggleChange={(vatApplied: any) => setFieldValue('percentage.vatable', vatApplied)}
                        vatApplied={values?.percentage?.vatable || false}
                        includeVatCalculator={false}
                      />
                    </FormField>
                  </div>
                  <div className="rbn--col-sm">
                    <FormField>
                      {/* Display-only: this field is disabled and derived from the split percentage,
                          so it must not write back to Formik (doing so on mount left the form dirty). */}
                      <CurrencyField
                        includeVatCalculator={false}
                        disabled
                        name={`${feeName}.splits.${i}.commission`}
                        label={i === 0 ? 'Commission' : undefined}
                        value={commissionAmount}
                        vatApplied={values?.netAmount?.vatable || false}
                      />
                    </FormField>
                  </div>
                  <div className={styles['select-primary-agent']}>
                    <FormField>
                      <ActionButton
                        tooltip={
                          isPrimary
                            ? 'Primary agent receives agent communication and is the reply-to contact for all tenant and owner communication for this lease.'
                            : 'Make primary'
                        }
                        icon={isPrimary ? 'primary-fill' : 'primary'}
                        action={isPrimary ? () => {} : () => handleSetPrimary(i)}
                      />
                    </FormField>
                  </div>
                </div>
              ),
            }
          })

          return (
            <>
              {!isEmpty(errors) && (
                <InfoBox type="error">
                  {Array.isArray(errors) ? map(err => <span>{err}</span>, errors) : errors}
                </InfoBox>
              )}
              {/* @todo create onMove function for Draggable List */}
              <DraggableList
                items={rows}
                itemStyle={{ height: '90px' }}
                onRemove={values.splits.length === 1 ? undefined : handleRemoveRow}
                onAdd={handleAddRow}
                addItemText="Add Agent"
                addItemIcon={true}
              />
            </>
          )
        }}
      />
    </div>
  )
}

export default CommissionSplit
